import { useRef, useEffect, useMemo, useCallback } from "react";
import throttle from "lodash/throttle";

const HOLD_THRESHOLD_MS = 1000;
const HEARTBEAT_INTERVAL_MS = 500;
const MOVE_THRESHOLD_PX = 3;

/**
 * Shared drag-and-drop / hold gesture layer for iOS WebRTC streams.
 *
 * Existing tap/click/swipe/longPress classifier stays in the caller. This hook
 * only owns the new hold-with-drag flow:
 *   - Arms a 1s timer on mousedown; on fire, sends mouse-longPress release:false
 *     and starts a 500ms heartbeat
 *   - Tracks pointer drift (>3px screen) to cancel the hold or mark it as
 *     drag-and-drop intent
 *   - On mouseup, sends mouse-longPress release:true (held in place) OR
 *     mouse-swipe release:true (drag-and-drop completed). Returns false if
 *     no hold fired so the caller's classifier runs.
 *
 * Caller wiring:
 *   const { armHold, closeOnMouseUp } = useGestureHold({ sendMessage });
 *   onMouseDown: handleCoordinate(e, (x, y) => { setX1Y1(x, y); armHold(e, x, y); })
 *   sendMouseEvent: if (closeOnMouseUp(x2, y2)) return;  // else fall through
 */
function useGestureHold({ sendMessage, isInteractionAllowed } = {}) {
  // Internal gesture state — refs only so timer/listener callbacks read current values
  const gestureActive = useRef(false);
  const stationary = useRef(true);
  const holdFired = useRef(false);
  const holdTimeout = useRef(null);
  const heartbeatInterval = useRef(null);
  const x1 = useRef(null);
  const y1 = useRef(null);
  const mousedownTime = useRef(0);
  // screen-space coords captured on mousedown — used for drift detection only
  const mousedownClientX = useRef(null);
  const mousedownClientY = useRef(null);
  const activeTarget = useRef(null);

  // mirror props into refs so memoized handlers always read the latest closure
  const sendMessageRef = useRef(sendMessage);
  sendMessageRef.current = sendMessage;
  const isInteractionAllowedRef = useRef(isInteractionAllowed);
  isInteractionAllowedRef.current = isInteractionAllowed;

  // Centralized teardown — kills every async/listener artifact this hook owns.
  // Called on mouseup, mouseleave, unmount, and any new armHold (which discards
  // leftover state before starting fresh).
  const reset = () => {
    if (holdTimeout.current) clearTimeout(holdTimeout.current);
    if (heartbeatInterval.current) clearInterval(heartbeatInterval.current);
    holdTimeout.current = null;
    heartbeatInterval.current = null;
    // flush any pending throttle trailing-edge call so it can't fire post-mouseup
    handleMouseMove?.cancel?.();
    stationary.current = true;
    holdFired.current = false;
    gestureActive.current = false;
    mousedownTime.current = 0;
    mousedownClientX.current = null;
    mousedownClientY.current = null;
    x1.current = null;
    y1.current = null;
  };

  const elapsed = () => {
    const ms = Date.now() - mousedownTime.current;
    return (Math.floor((ms / 1000) * 100) / 100).toString();
  };

  const safeSend = (payload) => {
    const allowed = isInteractionAllowedRef.current;
    if (typeof allowed === "function" && !allowed()) return;
    if (typeof sendMessageRef.current === "function") {
      sendMessageRef.current(payload);
    }
  };

  // ---- Payload senders (single source of truth for message shapes) ----

  const sendLongPress = (release) => {
    if (x1.current === null || y1.current === null) return;
    safeSend({
      mode: "mouse-longPress",
      x: x1.current.toString(),
      y: y1.current.toString(),
      duration: elapsed(),
      release,
    });
  };

  const sendSwipeRelease = (x2, y2) => {
    safeSend({
      mode: "mouse-swipe",
      x1: x1.current.toString(),
      y1: y1.current.toString(),
      x2: x2.toString(),
      y2: y2.toString(),
      delay: elapsed(),
      release: true,
    });
  };

  const sendHeartbeat = () => sendLongPress(false);

  const fireHold = () => {
    if (!stationary.current) return;
    if (x1.current === null || y1.current === null) return;
    holdFired.current = true;
    sendLongPress(false);
    heartbeatInterval.current = setInterval(
      sendHeartbeat,
      HEARTBEAT_INTERVAL_MS
    );
  };

  // stable identity so addEventListener / removeEventListener match
  const handleMouseMove = useMemo(
    () =>
      throttle((e) => {
        if (!gestureActive.current) return;
        if (
          mousedownClientX.current === null ||
          mousedownClientY.current === null
        ) {
          return;
        }
        if (!stationary.current) return;
        const dx = Math.abs(e.clientX - mousedownClientX.current);
        const dy = Math.abs(e.clientY - mousedownClientY.current);
        if (dx <= MOVE_THRESHOLD_PX && dy <= MOVE_THRESHOLD_PX) return;

        stationary.current = false;
        // cancel pending hold if it hasn't fired yet; if it has,
        // leave heartbeat running so mouseup closes with swipe release:true
        if (!holdFired.current && holdTimeout.current) {
          clearTimeout(holdTimeout.current);
          holdTimeout.current = null;
        }
      }, 50),
    []
  );

  const handleMouseLeave = useCallback(() => {
    if (!gestureActive.current) return;
    if (holdFired.current) sendLongPress(true);
    detach();
    reset();
  }, []);

  const detach = () => {
    const t = activeTarget.current;
    if (t) {
      t.removeEventListener("mousemove", handleMouseMove);
      t.removeEventListener("mouseleave", handleMouseLeave);
      activeTarget.current = null;
    }
  };

  // ---------------- Public API ----------------

  /**
   * Call from onMouseDown after the caller has computed and stored the
   * device-space coords. ctrl+hold is a no-op (matches existing classifier).
   */
  const armHold = (e, deviceX, deviceY) => {
    reset();
    if (!e) return;
    if (e.ctrlKey) return;
    if (deviceX === null || deviceY === null || deviceX === undefined || deviceY === undefined) {
      return;
    }
    x1.current = deviceX;
    y1.current = deviceY;
    mousedownTime.current = Date.now();
    mousedownClientX.current = e.clientX;
    mousedownClientY.current = e.clientY;
    activeTarget.current = e.currentTarget;
    e.currentTarget.addEventListener("mousemove", handleMouseMove);
    e.currentTarget.addEventListener("mouseleave", handleMouseLeave);
    gestureActive.current = true;
    holdTimeout.current = setTimeout(fireHold, HOLD_THRESHOLD_MS);
  };

  const hasValidCoords = (x2, y2) =>
    x1.current !== null &&
    y1.current !== null &&
    x2 !== null &&
    y2 !== null &&
    x2 !== undefined &&
    y2 !== undefined;

  /**
   * Call from sendMouseEvent / mouseup before the caller's existing classifier.
   * Returns true if this hook sent the close message (longPress release:true or
   * swipe release:true). Returns false when no hold ever fired — caller should
   * run the existing tap/swipe/longPress classifier.
   */
  const closeOnMouseUp = (x2, y2) => {
    const didFireHold = holdFired.current;
    const wasStationary = stationary.current;
    detach();

    if (!didFireHold) {
      reset();
      return false;
    }
    if (hasValidCoords(x2, y2)) {
      if (wasStationary) sendLongPress(true);
      else sendSwipeRelease(x2, y2);
    }
    reset();
    return true;
  };

  useEffect(
    () => () => {
      if (holdFired.current) sendLongPress(true);
      detach();
      reset();
      handleMouseMove?.cancel?.();
    },
    [handleMouseMove]
  );

  return { armHold, closeOnMouseUp };
}

export default useGestureHold;
